import { get } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import type {
  ClientResultApiResponse,
  ConsolidatedFinancialData,
  DirectorTarget,
  GlobalPerformanceMetrics,
  MonthlyDataPoint,
  MyClientResponse
} from '@/types/financial'

const API_NAME = 'monumenta'

/**
 * Fetch client revenue data with PROA projections
 * Returns client-level financial data including actual revenue and budget targets
 *
 * @param year - Target year ('2024' | '2025')
 * @param options - Optional filtering and pagination parameters
 * @returns Array of client financial results
 */
export const getClients = async (
  year: string,
  options?: {
    limit?: number
    directorEmail?: string
    directorName?: string
  }
): Promise<ClientResultApiResponse[]> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)

  if (options?.limit) queryParams.append('limit', options.limit.toString())
  if (options?.directorEmail) queryParams.append('directorEmail', options.directorEmail)
  if (options?.directorName) queryParams.append('directorName', options.directorName)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/sig-data/clients?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as ClientResultApiResponse[]
}

/**
 * Fetch consolidated financial data with real/proa/variance structure
 * Returns company-wide financial metrics across all categories
 *
 * @param year - Target year ('2024' | '2025')
 * @param month - Optional month filter ('Jan', 'Feb', etc.)
 * @returns Consolidated financial data with variance calculations
 */
export const getConsolidatedData = async (
  year: string,
  month?: string
): Promise<ConsolidatedFinancialData> => {
  // If month is not provided, use the yearly aggregation endpoint
  if (!month) {
    const queryParams = new URLSearchParams()
    queryParams.append('year', year)

    const restOperation = get({
      apiName: API_NAME,
      path: `/monumenta/sig-data/consolidated/yearly?${queryParams.toString()}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()

    return data as unknown as ConsolidatedFinancialData
  }

  // Monthly endpoint - requires both year and month
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)
  queryParams.append('month', month)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/sig-data/consolidated?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as ConsolidatedFinancialData
}

/**
 * Fetch monthly financial data for a specific client
 * Returns time-series data for charting and analysis
 *
 * @param year - Target year ('2024' | '2025')
 * @param clientCode - Client identifier code
 * @returns Array of monthly data points
 */
export const getClientMonthly = async (
  year: string,
  clientCode: string
): Promise<MonthlyDataPoint[]> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/sig-data/clients/${clientCode}/monthly?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as MonthlyDataPoint[]
}

/**
 * Fetch consolidated monthly time-series data
 * Returns monthly revenue and costs for the entire year
 *
 * @param year - Target year ('2024' | '2025')
 * @returns Array of monthly consolidated data points
 */
export const getConsolidatedMonthly = async (
  year: string
): Promise<Array<{ month: string; receita: number; custos: number }>> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/sig-data/consolidated/monthly-series?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as Array<{ month: string; receita: number; custos: number }>
}

/**
 * Fetch director targets for PROA budget planning
 * Returns target assignments for directors with client allocations
 *
 * @param year - Target year ('2024' | '2025')
 * @param directorEmail - Optional filter by director email
 * @returns Array of director targets
 */
export const getDirectorTargets = async (
  year: string,
  directorEmail?: string
): Promise<DirectorTarget[]> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)

  if (directorEmail) queryParams.append('directorEmail', directorEmail)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/performance/director-targets?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as DirectorTarget[]
}

/**
 * Fetch pre-calculated global PROA performance metrics
 * Returns aggregated performance data calculated server-side
 * Reduces frontend computation and network overhead
 *
 * @param year - Target year ('2024' | '2025')
 * @returns Global performance metrics with totals and percentage
 */
export const getGlobalPerformance = async (
  year: string
): Promise<GlobalPerformanceMetrics> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/sig-data/global-performance?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as GlobalPerformanceMetrics
}

/**
 * Fetch MY client data based on authenticated user's employeeId
 * - Admins see all clients with full data
 * - Directors see only their assigned clients with limited fields
 *
 * Server-side filters clients based on EMPLOYEE_CLIENT_MAPPING
 * Eliminates need for frontend filtering logic
 *
 * @param year - Target year ('2024' | '2025')
 * @returns Array of client data (full for admins, limited for directors)
 */
export const getMyClients = async (
  year: string
): Promise<ClientResultApiResponse[] | MyClientResponse[]> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year)

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/sig-data/my-clients?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as ClientResultApiResponse[] | MyClientResponse[]
}
